import { useMutation, useQuery } from '@apollo/client';

import deleteLearningPathMutation from 'bundles/enterprise-admin-learning-path/mutations/DeleteLearningPathMetadata.graphql';
import updateLearningPathActivitiesMutation from 'bundles/enterprise-admin-learning-path/mutations/UpdateLearningPathActivities.graphql';
import updateLearningPathMetadataMutation from 'bundles/enterprise-admin-learning-path/mutations/UpdateLearningPathMetadata.graphql';
import type {
  DeleteLearningPathMutation,
  DeleteLearningPathMutationVariables,
} from 'bundles/enterprise-admin-learning-path/mutations/__generated__/DeleteLearningPathMetadata';
import type {
  UpdateLearningPathActivitiesMutation,
  UpdateLearningPathActivitiesMutationVariables,
} from 'bundles/enterprise-admin-learning-path/mutations/__generated__/UpdateLearningPathActivities';
import type {
  UpdateLearningPathMetadataMutation,
  UpdateLearningPathMetadataMutationVariables,
} from 'bundles/enterprise-admin-learning-path/mutations/__generated__/UpdateLearningPathMetadata';
import countByBadgeQuery from 'bundles/enterprise-admin-learning-path/queries/CountByBadge.graphql';
import badgeQuery from 'bundles/enterprise-admin-learning-path/queries/EnterpriseBadge.graphql';
import type {
  CountByBadgeQuery,
  CountByBadgeQueryVariables,
} from 'bundles/enterprise-admin-learning-path/queries/__generated__/CountByBadge';
import type {
  EnterpriseBadgeQuery,
  EnterpriseBadgeQueryVariables,
} from 'bundles/enterprise-admin-learning-path/queries/__generated__/EnterpriseBadge';

export const useEnterpriseBadge = (programId?: string, badgeTemplateId?: string) => {
  const { data, loading, error } = useQuery<EnterpriseBadgeQuery, EnterpriseBadgeQueryVariables>(badgeQuery, {
    skip: !programId || !badgeTemplateId,
    variables: {
      programId: String(programId),
      badgeTemplateId: String(badgeTemplateId),
    },
    context: {
      clientName: 'gatewayGql',
    },
    fetchPolicy: 'network-only',
  });
  const badge = data?.EnterpriseBadge?.findByProgramAndBadgeTemplateId?.enterpriseBadge;
  return { badge, loading, error };
};

export const useLearningPathMetadataMutation = () => {
  return useMutation<UpdateLearningPathMetadataMutation, UpdateLearningPathMetadataMutationVariables>(
    updateLearningPathMetadataMutation,
    {
      context: { clientName: 'gatewayGql' },
    }
  );
};

export const useLearningPathActivitiesMutation = () => {
  return useMutation<UpdateLearningPathActivitiesMutation, UpdateLearningPathActivitiesMutationVariables>(
    updateLearningPathActivitiesMutation,
    {
      context: { clientName: 'gatewayGql' },
    }
  );
};

export const useDeleteLearningPathMutation = () => {
  return useMutation<DeleteLearningPathMutation, DeleteLearningPathMutationVariables>(deleteLearningPathMutation, {
    context: { clientName: 'gatewayGql' },
  });
};

export const useCountByBadgeQuery = (badgeTemplateId?: string, programId?: string) => {
  const { data, loading, error } = useQuery<CountByBadgeQuery, CountByBadgeQueryVariables>(countByBadgeQuery, {
    skip: !badgeTemplateId || !programId,
    variables: {
      badgeTemplateId: badgeTemplateId as string,
      programId: programId as string,
      userStates: ['ENROLLED', 'ISSUED'],
    },
    context: { clientName: 'gatewayGql' },
  });
  const counts = data?.Badge?.queryCountByBadgeTemplateAndStates.result;
  const enrollmentCount = loading ? undefined : counts?.find((count) => count.userState === 'ENROLLED')?.count ?? 0;
  const completionCount = loading ? undefined : counts?.find((count) => count.userState === 'ISSUED')?.count ?? 0;
  return { enrollmentCount, completionCount, loadingCount: loading, countError: error };
};
